import type { Dayjs, OpUnitType } from 'dayjs';

/** From (0) and To (1); the index matches the antd range field index. */
export type RangeEndpoint = 0 | 1;

export type EndpointRole = 'start' | 'end';

export type RangeValue = readonly [Dayjs, Dayjs];

export type PartialRangeValue = readonly [Dayjs | null, Dayjs | null];

interface DisabledTimeConstraints {
  readonly disabledHours?: () => number[];
  readonly disabledMinutes?: (hour: number) => number[];
  readonly disabledSeconds?: (hour: number, minute: number) => number[];
}

/** Everything the reducer must check before it may call a candidate a valid change. */
export interface RangePickerConstraints {
  readonly disabledDate?: (date: Dayjs, info: { type: 'date'; from?: Dayjs }) => boolean;
  readonly disabledTime?: (date: Dayjs, role: EndpointRole) => DisabledTimeConstraints;
  readonly minDate?: Dayjs;
  readonly maxDate?: Dayjs;
  readonly disabledEndpoints?: readonly [boolean, boolean];
  readonly allowClear?: boolean;
  readonly precision?: OpUnitType;
}

export interface RangePickerState {
  /** Last interval represented externally (emitted or received). */
  readonly applied: RangeValue | null;
  /** Normalised edit shown in the picker until OK applies it; null means nothing pending. */
  readonly draft: PartialRangeValue | null;
  /** Parsed keyboard entry awaiting Enter or blur before it joins the draft. */
  readonly typed: PartialRangeValue | null;
  readonly active: RangeEndpoint;
  readonly open: boolean;
}

export type RangePickerEvent =
  | { readonly type: 'open' }
  | { readonly type: 'close' }
  | { readonly type: 'confirm' }
  | { readonly type: 'focus'; readonly endpoint: RangeEndpoint }
  | { readonly type: 'calendarChange'; readonly range: PartialRangeValue; readonly source: 'panel' | 'typed' }
  | { readonly type: 'accept'; readonly source: 'key' | 'blur' }
  | { readonly type: 'preset'; readonly range: RangeValue }
  | { readonly type: 'clear' }
  | { readonly type: 'externalValue'; readonly range: RangeValue | null };

export type RangePickerEffect =
  | { readonly type: 'emit'; readonly range: RangeValue | null }
  | { readonly type: 'focus'; readonly endpoint: RangeEndpoint }
  | { readonly type: 'blur' };

interface RangePickerResult {
  readonly state: RangePickerState;
  readonly effects: readonly RangePickerEffect[];
}

interface EndpointCheck {
  readonly date: Dayjs;
  readonly endpoint: RangeEndpoint;
  readonly from: Dayjs | undefined;
  readonly constraints: RangePickerConstraints;
}

const DEFAULT_PRECISION: OpUnitType = 'minute';
const NO_EFFECTS: readonly RangePickerEffect[] = [];
// Closing itself travels in `state.open`; only the DOM blur has to be replayed by the adapter.
const CLOSE_EFFECTS: readonly RangePickerEffect[] = [{ type: 'blur' }];

const otherEndpoint = (endpoint: RangeEndpoint): RangeEndpoint => (endpoint === 0 ? 1 : 0);

const roleOf = (endpoint: RangeEndpoint): EndpointRole => (endpoint === 0 ? 'start' : 'end');

const precisionOf = (constraints: RangePickerConstraints): OpUnitType => constraints.precision ?? DEFAULT_PRECISION;

const emitOf = (range: RangeValue | null): RangePickerEffect => ({ type: 'emit', range });

const noEffects = (state: RangePickerState): RangePickerResult => ({ state, effects: NO_EFFECTS });

/**
 * Pair the picker shows: the pending draft while there is one, else the applied range.
 * @param state current state
 */
export const shownRange = (state: RangePickerState): PartialRangeValue | null => state.draft ?? state.applied;

function isSameDate(left: Dayjs | null | undefined, right: Dayjs | null | undefined, precision: OpUnitType): boolean {
  if (!left || !right) return !left && !right;
  return left.isSame(right, precision);
}

/**
 * Maps the antd field role onto the endpoint index the model works with.
 * @param role role antd reports for the focused field
 * @returns endpoint index, or null when antd reported no field
 */
export function endpointOf(role: EndpointRole | undefined): RangeEndpoint | null {
  if (role === 'start') return 0;
  if (role === 'end') return 1;
  return null;
}

/**
 * Compares two optional pairs slot by slot at the given precision; an empty pair equals a pair of empty slots.
 * @param left first pair, or empty
 * @param right second pair, or empty
 * @param precision smallest unit that still counts as a difference
 */
export function isSameRange(left: PartialRangeValue | null, right: PartialRangeValue | null, precision: OpUnitType): boolean {
  return isSameDate(left?.[0], right?.[0], precision) && isSameDate(left?.[1], right?.[1], precision);
}

function mergePair(raw: PartialRangeValue, base: PartialRangeValue | null): PartialRangeValue {
  return [raw[0] ?? base?.[0] ?? null, raw[1] ?? base?.[1] ?? null];
}

function isComplete(value: PartialRangeValue): value is RangeValue {
  return !!value[0] && !!value[1];
}

function normalize(value: RangeValue, precision: OpUnitType): { range: RangeValue; swapped: boolean } {
  const swapped = value[0].isAfter(value[1], precision);
  return { range: swapped ? [value[1], value[0]] : value, swapped };
}

function isTimeAllowed(date: Dayjs, role: EndpointRole, constraints: RangePickerConstraints): boolean {
  const times = constraints.disabledTime?.(date, role);
  if (!times) return true;
  const hour = date.hour();
  const minute = date.minute();
  if (times.disabledHours?.().includes(hour)) return false;
  if (times.disabledMinutes?.(hour).includes(minute)) return false;
  return !times.disabledSeconds?.(hour, minute).includes(date.second());
}

function isEndpointAllowed({ date, endpoint, from, constraints }: EndpointCheck): boolean {
  const precision = precisionOf(constraints);
  if (constraints.minDate && date.isBefore(constraints.minDate, precision)) return false;
  if (constraints.maxDate && date.isAfter(constraints.maxDate, precision)) return false;
  if (constraints.disabledDate?.(date, { type: 'date', from })) return false;
  return isTimeAllowed(date, roleOf(endpoint), constraints);
}

/** Slots whose value the candidate would actually write compared to the applied range. */
function changedSlots(range: RangeValue, applied: RangeValue | null, precision: OpUnitType): RangeEndpoint[] {
  const slots: RangeEndpoint[] = [];
  if (!applied || !isSameDate(range[0], applied[0], precision)) slots.push(0);
  if (!applied || !isSameDate(range[1], applied[1], precision)) slots.push(1);
  return slots;
}

function isCandidateValid(range: RangeValue, applied: RangeValue | null, constraints: RangePickerConstraints): boolean {
  const slots = changedSlots(range, applied, precisionOf(constraints));
  if (slots.some(slot => constraints.disabledEndpoints?.[slot])) return false;
  return slots.every(slot => isEndpointAllowed({
    date: range[slot],
    endpoint: slot,
    from: slot === 1 ? range[0] : undefined,
    constraints,
  }));
}

/** Folds a raw pair into the draft: validated, swap-normalised, never emitted. */
function draftEdit(state: RangePickerState, raw: PartialRangeValue, constraints: RangePickerConstraints): RangePickerResult {
  const precision = precisionOf(constraints);
  const merged = mergePair(raw, shownRange(state));
  const cleared = { ...state, typed: null };
  if (!isComplete(merged)) return noEffects({ ...cleared, draft: merged });
  const { range, swapped } = normalize(merged, precision);
  if (!isCandidateValid(range, state.applied, constraints)) return noEffects(cleared);
  if (isSameRange(range, state.applied, precision)) return noEffects({ ...cleared, draft: null });
  const active = swapped ? otherEndpoint(state.active) : state.active;
  return { state: { ...cleared, draft: range, active }, effects: swapped ? [{ type: 'focus', endpoint: active }] : NO_EFFECTS };
}

function acceptTyped(state: RangePickerState, constraints: RangePickerConstraints): RangePickerResult {
  return state.typed ? draftEdit(state, state.typed, constraints) : noEffects(state);
}

function closeSession(state: RangePickerState): RangePickerState {
  return { ...state, open: false, draft: null, typed: null };
}

function confirmDraft(state: RangePickerState, constraints: RangePickerConstraints): RangePickerResult {
  const { state: accepted, effects } = acceptTyped(state, constraints);
  const { draft, applied } = accepted;
  const applies = !!draft && isComplete(draft) && isCandidateValid(draft, applied, constraints);
  if (!applies) return { state: closeSession(accepted), effects: [...effects, ...CLOSE_EFFECTS] };
  return { state: { ...closeSession(accepted), applied: draft }, effects: [...effects, emitOf(draft), ...CLOSE_EFFECTS] };
}

function applyPreset(state: RangePickerState, value: RangeValue, constraints: RangePickerConstraints): RangePickerResult {
  const precision = precisionOf(constraints);
  const { range } = normalize(value, precision);
  const closed = closeSession(state);
  const skip = !isCandidateValid(range, state.applied, constraints) || isSameRange(range, state.applied, precision);
  if (skip) return { state: closed, effects: CLOSE_EFFECTS };
  return { state: { ...closed, applied: range }, effects: [emitOf(range), ...CLOSE_EFFECTS] };
}

function applyClear(state: RangePickerState, constraints: RangePickerConstraints): RangePickerResult {
  if (!constraints.allowClear) return noEffects(state);
  const cleared = { ...state, applied: null, draft: null, typed: null };
  return { state: cleared, effects: state.applied ? [emitOf(null)] : NO_EFFECTS };
}

/**
 * Builds the initial state of an edit session.
 * @param applied range the consumer starts with
 */
export function createRangePickerState(applied: RangeValue | null = null): RangePickerState {
  return { applied, draft: null, typed: null, active: 0, open: false };
}

/**
 * Pure interaction model of the deferred-apply range picker: edits build a draft, OK emits it once.
 * @param state current state
 * @param event interaction reported by the adapter
 * @param constraints validation inputs of the current render
 * @returns next state plus the effects the adapter has to run
 */
export function rangePickerReducer(
  state: RangePickerState,
  event: RangePickerEvent,
  constraints: RangePickerConstraints = {},
): RangePickerResult {
  switch (event.type) {
    case 'open':
      return state.open ? noEffects(state) : noEffects({ ...state, open: true, draft: null, typed: null });
    case 'close':
      return state.open ? { state: closeSession(state), effects: CLOSE_EFFECTS } : noEffects(state);
    case 'confirm':
      return state.open ? confirmDraft(state, constraints) : noEffects(state);
    case 'focus':
      return state.active === event.endpoint ? noEffects(state) : noEffects({ ...state, active: event.endpoint });
    case 'calendarChange':
      if (!state.open) return noEffects(state);
      if (event.source === 'typed') return noEffects({ ...state, typed: event.range });
      return draftEdit(state, event.range, constraints);
    case 'accept': {
      const accepted = acceptTyped(state, constraints);
      // A swap on the way out must not pull focus back into the picker the user is leaving.
      if (event.source !== 'blur') return accepted;
      return { state: accepted.state, effects: accepted.effects.filter(effect => effect.type !== 'focus') };
    }
    case 'preset':
      return applyPreset(state, event.range, constraints);
    case 'clear':
      return applyClear(state, constraints);
    case 'externalValue':
      if (isSameRange(event.range, state.applied, precisionOf(constraints))) return noEffects(state);
      return noEffects({ ...state, applied: event.range, draft: null, typed: null });
  }
}

/**
 * Range a picker would hold if the hovered date were picked for the active endpoint.
 * @param current pair the picker currently shows
 * @param active endpoint the hovered cell would be written to
 * @param hoverDate hovered cell, or null while nothing is hovered
 * @returns preview range, or null while it cannot be formed
 */
export function candidateRange(current: PartialRangeValue | null, active: RangeEndpoint, hoverDate: Dayjs | null): RangeValue | null {
  if (!hoverDate || !current) return null;
  const other = current[otherEndpoint(active)];
  if (!other) return null;
  const base = current[active];
  const candidate = base ? base.year(hoverDate.year()).month(hoverDate.month()).date(hoverDate.date()) : hoverDate;
  const value: RangeValue = active === 0 ? [candidate, other] : [other, candidate];
  return normalize(value, DEFAULT_PRECISION).range;
}
